import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useViewMode } from '../../context/ViewModeContext'; // ⬅️ Ajout important

const Sidebar = ({ isOpen = false, onClose = () => {} }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { mode } = useViewMode(); // ⬅️ On récupère le mode

  let currentUser = user;
  if (!currentUser) {
    try {
      const stored = localStorage.getItem('user');
      if (stored) currentUser = JSON.parse(stored);
    } catch (e) {
      currentUser = null;
    }
  }

  const role = currentUser?.role || 'admin';

  // ═══════════════════════════════════════════════════════════════
  // MENUS PAR RÔLE
  // ═══════════════════════════════════════════════════════════════
  const menuByRole = {
    admin: [
      { path: '/admin', icon: '🏠', label: 'Tableau de bord' },
      { path: '/admin/students', icon: '👥', label: 'Élèves' },
      { path: '/admin/teachers', icon: '👤', label: 'Professeurs' },
      { path: '/admin/classes', icon: '👨‍👩‍👧', label: 'Classes' },
      { path: '/admin/subjects', icon: '📚', label: 'Matières' },
      { path: '/admin/courses', icon: '🎥', label: 'Cours' },
      { path: '/admin/compositions', icon: '📝', label: 'Compositions' },
      { path: '/admin/schedule', icon: '🕐', label: 'Emploi du temps' },
      { path: '/admin/bulletins', icon: '📄', label: 'Bulletins' }, // ⬅️ LA LIGNE À AJOUTER
      { path: '/admin/absences', icon: '📅', label: 'Absences' },
      { path: '/admin/payments', icon: '💳', label: 'Paiements' },
      { path: '/admin/enrollments', icon: '🎓', label: 'Passation de classe' },
      { path: '/admin/invitations', icon: '📧', label: 'Invitations' },
      { path: '/admin/year-preparation', icon: '📅', label: 'Préparation rentrée' },
    ],
    super_admin: [
      { path: '/super-admin', icon: '🏠', label: 'Tableau de bord' },
      { path: '/super-admin/establishments', icon: '🏫', label: 'Établissements' },
      { path: '/super-admin/establishments/new', icon: '➕', label: 'Nouveau lycée' },
    ],
    teacher: [
      { path: '/teacher', icon: '🏠', label: 'Tableau de bord' },
      { path: '/teacher/my-class', icon: '🎯', label: 'Ma classe principale' },
      { path: '/teacher/teacher-attendance', icon: '🕐', label: 'Présence profs' },
      { path: '/teacher/classes', icon: '👨‍👩‍👧', label: 'Mes classes' },
      { path: '/teacher/students', icon: '👥', label: 'Mes élèves' },
      { path: '/teacher/grades', icon: '📝', label: 'Notes' },
      { path: '/teacher/attendance', icon: '📅', label: 'Appel' },
      { path: '/teacher/homework', icon: '📚', label: 'Devoirs' },
      { path: '/teacher/exams', icon: '📋', label: 'Compositions' },
      { path: '/teacher/schedule', icon: '🕐', label: 'Emploi du temps' },
      { path: '/teacher/messages', icon: '💬', label: 'Messages' },
      { path: '/teacher/profile', icon: '⚙️', label: 'Mon profil' },
    ],
    principal_teacher: [
      { path: '/teacher', icon: '🏠', label: 'Tableau de bord' },
      { path: '/teacher/my-class', icon: '🎯', label: 'Ma classe principale' },
      { path: '/teacher/teacher-attendance', icon: '🕐', label: 'Présence profs' },
      { path: '/teacher/teacher-attendance-history', icon: '📜', label: 'Historique présences' },
      { path: '/teacher/classes', icon: '👨‍👩‍👧', label: 'Mes classes' },
      { path: '/teacher/students', icon: '👥', label: 'Mes élèves' },
      { path: '/teacher/grades', icon: '📝', label: 'Notes' },
      { path: '/teacher/attendance', icon: '📅', label: 'Appel' },
      { path: '/teacher/homework', icon: '📚', label: 'Devoirs' },
      { path: '/teacher/exams', icon: '📋', label: 'Compositions' },
      { path: '/teacher/schedule', icon: '🕐', label: 'Emploi du temps' },
      { path: '/teacher/messages', icon: '💬', label: 'Messages' },
      { path: '/teacher/profile', icon: '⚙️', label: 'Mon profil' },
    ],
    student: [
      { path: '/student', icon: '🏠', label: 'Tableau de bord' },
      { path: '/student/courses', icon: '📚', label: 'Mes cours' },
      { path: '/student/grades', icon: '📝', label: 'Mes notes' },
      { path: '/student/absences', icon: '📅', label: 'Mes absences' },
      { path: '/student/homework', icon: '📚', label: 'Mes devoirs' },
      { path: '/student/schedule', icon: '🕐', label: 'Mon emploi du temps' },
      { path: '/student/reports', icon: '📊', label: 'Mes résultats' },
      { path: '/student/messages', icon: '💬', label: 'Messages' },
      { path: '/student/profile', icon: '👤', label: 'Profil' },
    ],
    parent: [
      { path: '/parent', icon: '🏠', label: 'Tableau de bord' },
      { path: '/parent/children', icon: '👦', label: 'Mes enfants' },
      { path: '/parent/schedule', icon: '🕐', label: 'Emploi du temps' },
      { path: '/parent/bulletins', icon: '📊', label: 'Bulletins' },
      { path: '/parent/absences', icon: '📅', label: 'Absences' },
      { path: '/parent/messages', icon: '💬', label: 'Messages' },
      { path: '/parent/profile', icon: '👤', label: 'Profil' },
    ],
  };

  const menuItems = menuByRole[role] || menuByRole.admin;

  const handleLogout = () => {
    if (logout) logout();
    else {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
    }
    navigate('/login');
  };

  const isActive = (path) => {
    const roots = ['/admin', '/teacher', '/student', '/parent', '/super-admin'];
    if (roots.includes(path)) {
      return location.pathname === path;
    }
    return location.pathname.startsWith(path);
  };

  const handleLinkClick = () => {
    if (onClose) onClose();
  };

  // On détermine si on est en mode téléphone forcé
  const isMobileMode = mode === 'mobile';

  return (
    <>
      {/* 🌑 Overlay sombre (seulement si ouvert en mode mobile) */}
      {isMobileMode && isOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40"
          onClick={onClose}
          aria-hidden="true"
        />
      )}

      {/* 📋 Sidebar */}
      <aside
        className={`
          w-64 bg-gradient-to-b from-[#0A0A0A] via-[#0A0A0A] to-[#1a1a1a] text-white
          flex flex-col h-screen min-h-0
          overflow-hidden relative flex-shrink-0
          z-50 transition-transform duration-300 ease-in-out
          ${isMobileMode ? 'fixed top-0 left-0' : 'md:sticky md:top-0 self-start'}
          ${isMobileMode 
            ? (isOpen ? 'translate-x-0' : '-translate-x-full') 
            : 'translate-x-0'
          }
        `}
      >
        <div className="absolute -bottom-32 -right-32 w-[300px] h-[300px] rounded-full bg-[#F39C12] opacity-20 blur-3xl pointer-events-none"></div>

        {/* En-tête : logo + bouton fermer sur mobile */}
        <div className="px-5 py-5 border-b border-white/10 relative z-10 shrink-0 flex items-center justify-between">
          <Link to="/" className="flex items-center" onClick={handleLinkClick}>
            <img src="/images/logo.png" alt="SamaEcole" className="h-14 w-auto object-contain" />
          </Link>

          {/* ✕ Bouton fermer — uniquement si mode téléphone */}
          {isMobileMode && (
            <button
              type="button"
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-white/10 text-gray-300"
              aria-label="Fermer le menu"
            >
              <CloseIcon className="w-5 h-5" />
            </button>
          )}
        </div>

        <nav className="flex-1 min-h-0 overflow-y-auto overscroll-contain py-4 relative z-10">
          <ul className="space-y-1 px-3">
            {menuItems.map((item, index) => {
              const active = isActive(item.path);
              return (
                <li key={`${item.path}-${index}`}>
                  <Link
                    to={item.path}
                    onClick={handleLinkClick}
                    className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm transition-all duration-200 ${
                      active
                        ? 'bg-gradient-to-r from-[#F39C12] to-[#E67E22] text-white font-semibold shadow-lg shadow-orange-500/30'
                        : 'text-gray-300 hover:bg-white/5 hover:text-white'
                    }`}
                  >
                    <span className="text-lg">{item.icon}</span>
                    <span>{item.label}</span>
                  </Link>
                </li>
              );
            })}
          </ul>
        </nav>

        <div className="border-t border-white/10 p-3 relative z-10 shrink-0">
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm text-gray-300 hover:bg-red-500/10 hover:text-red-400 transition-all"
          >
            <span className="text-lg">🚪</span>
            <span>Déconnexion</span>
          </button>
        </div>
      </aside>
    </>
  );
};

// ═══════════ ICÔNE SVG ═══════════
const CloseIcon = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

export default Sidebar;